import { ClientEntity } from 'src/app/models/clients';
import {
  AssetPerformanceRequest,
  IAssetPerformanceDataResult,
  IPerformance,
  IPerformanceDataResult,
} from './../../models/performance';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base.http.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';

import * as _moment from 'moment';
import * as _rollupMoment from 'moment';
import { AppConfig } from 'src/app/app.config';
import { IEnvironmentConfig } from 'src/app/models/environmentConfig';

const moment = _rollupMoment || _moment;

@Injectable()
export class PerformanceService extends BaseHttpService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfig
  ) {
    super();
  }

  get envSettings(): IEnvironmentConfig {
    return this.appConfig.getEnvironmentConfig();
  }

  private getDateFromCCYYMMDD(dateValue: any) {
    if (dateValue !== undefined) {
      return typeof dateValue === 'string'
        ? new Date(Date.parse(dateValue))
        : dateValue;
    } else {
      return new Date();
    }
  }

  public getPerformanceData(request: IPerformance): Observable<IPerformanceDataResult> {

    const date = this.parseDate(this.getDateFromCCYYMMDD(request.date));
    const url = new URL(`${this.envSettings.webApiUrl}portfolio/performance/${request.returnType}/${date}`);

    url.searchParams.append('period', request.returnPeriod);

    request.clientEntities.forEach(entity => {
      url.searchParams.append('entities', entity.EntityId.toString());
    });

    let returnSpanMonths = request.returnSpanMonths;
    // Get portfolio performance for custom date range
    if (returnSpanMonths == -9 && request.fromDate !== undefined) {
      returnSpanMonths = -1;
      url.searchParams.append('fromDate', this.parseDate(this.getDateFromCCYYMMDD(request.fromDate)));
    }

    if (returnSpanMonths) {
      url.searchParams.append('spanMonths', returnSpanMonths.toString());
    }

    // PortfolioId filters.
    if (request.portfolios && request.portfolios.length > 0) {
      for (const portfolio of request.portfolios) {
        url.searchParams.append('portfolios', portfolio.id.toString());
      }
    }

    return this.http
      .get(url.toString(), {
        observe: "body",
        responseType: "json",
        headers: this.getHttpHeaders()
      })
      .pipe(
        map(res => {
          const data: IPerformanceDataResult = {
            performanceData: res,
            date: request.date,
            clientEntities: request.clientEntities,
            portfolios: request.portfolios,
          };
          return data;
        })
      );
  }

  public getAssetPerformanceData(
    request: AssetPerformanceRequest
  ): Observable<IAssetPerformanceDataResult> {
    const url = new URL(`${this.envSettings.webApiUrl}assetperformance`);

    // Append toDate query param.
    const toDate = this.parseDate(this.getDateFromCCYYMMDD(request.toDate));
    url.searchParams.append('toDate', toDate);

    // Append entityId query param(s).
    const entityIds = request.clientEntities.map(
      (selectedEntity: ClientEntity) => selectedEntity.EntityId
    );
    entityIds.forEach(entityId => {
      url.searchParams.append('entities', entityId.toString());
    });

    // Append fromDate query param.
    let returnSpanMonths = request.returnSpanMonths;
    // Get asset performance for custom date range
    if (returnSpanMonths <= 0 && request.fromDate !== undefined) {
      returnSpanMonths = -1;
      url.searchParams.append('fromDate', this.parseDate(this.getDateFromCCYYMMDD(request.fromDate)));
    }

    // Append spanMonths query param.
    url.searchParams.append('spanMonths', returnSpanMonths.toString());

    // Append portfolios query param(s).
    if (request.portfolios && request.portfolios.length > 0) {
      for (const portfolio of request.portfolios) {
        url.searchParams.append('portfolios', portfolio.id.toString());
      }
    }

    return this.http
      .get(url.toString(), {
        observe: "body",
        responseType: "json",
        headers: this.getHttpHeaders()
      })
      .pipe(
        map(res => {
          return {
            assetPerformanceData: res,
            date: request.toDate,
            clientEntities: request.clientEntities,
            portfolios: request.portfolios,
          } as IAssetPerformanceDataResult;
        })
      );
  }

  public getAssetClassificationPerformanceData(
    request: AssetPerformanceRequest
  ): Observable<IAssetPerformanceDataResult> {
    const url = new URL(`${this.envSettings.webApiUrl}asset-classification-performance`);

    // Append toDate query param.
    const toDate = this.parseDate(this.getDateFromCCYYMMDD(request.toDate));
    url.searchParams.append('toDate', toDate);

    // Append entityId query param(s).
    const entityIds = request.clientEntities.map(
      (selectedEntity: ClientEntity) => selectedEntity.EntityId
    );
    entityIds.forEach(entityId => {
      url.searchParams.append('entities', entityId.toString());
    });

    // Append fromDate query param.
    let returnSpanMonths = request.returnSpanMonths;
    // Get asset performance for custom date range
    if (returnSpanMonths <= 0 && request.fromDate !== undefined) {
      returnSpanMonths = -1;
      url.searchParams.append('fromDate', this.parseDate(this.getDateFromCCYYMMDD(request.fromDate)));
    }

    // Append spanMonths query param.
    url.searchParams.append('spanMonths', returnSpanMonths.toString());

    // Append portfolios query param(s).
    if (request.portfolios && request.portfolios.length > 0) {
      for (const portfolio of request.portfolios) {
        url.searchParams.append('portfolios', portfolio.id.toString());
      }
    }

    return this.http
      .get(url.toString(), {
        observe: "body",
        responseType: "json",
        headers: this.getHttpHeaders()
      })
      .pipe(
        map(res => {
          return {
            assetPerformanceData: res,
            date: request.toDate,
            clientEntities: request.clientEntities,
            portfolios: request.portfolios,
          } as IAssetPerformanceDataResult;
        })
      );
  }
}
